import {
  KANBAN_ACCESS_PERMISSIONS,
  KANBAN_ANALYTICS_BLOCK_PERMISSIONS,
  KANBAN_DEAL_MANAGE_PERMISSIONS,
  KANBAN_MANAGE_PERMISSIONS,
  KANBAN_OWN_DEALS_ONLY_PERMISSIONS,
} from 'dashboard/constants/permissions';

// Espelho de app/policies/kanban_permissions.rb — mesma tabela, mesma
// ordem, mesma regra pro atendente sem papel. Quem mudar um muda o outro.
//
//   kanban_access       ver menu, quadro, analytics, cards (leitura)
//   kanban_deal_manage  criar, editar, mover, arquivar cards
//   kanban_manage       funil, fase, automação, passagem, agendamentos, excluir card
//
// Fora da escada: 'kanban_analytics_block' tira o Analytics de um papel que vê
// o quadro. Presença bloqueia; nunca pega admin nem atendente sem papel.
// 'kanban_own_deals_only' recorta o quadro pros cards em que a pessoa atende
// (Deals::Visibility no backend) e também esconde o Analytics, que conta o
// funil inteiro.
//
// `permissions` é a lista do usuário na conta (payload do _user.json):
// ['administrator'] pro admin, ['agent'] pro atendente sem papel, e a lista do
// papel personalizado (+ 'custom_role') pros demais.
const IMPLIED_BY = {
  [KANBAN_ACCESS_PERMISSIONS]: [
    KANBAN_ACCESS_PERMISSIONS,
    KANBAN_DEAL_MANAGE_PERMISSIONS,
    KANBAN_MANAGE_PERMISSIONS,
  ],
  [KANBAN_DEAL_MANAGE_PERMISSIONS]: [
    KANBAN_DEAL_MANAGE_PERMISSIONS,
    KANBAN_MANAGE_PERMISSIONS,
  ],
  [KANBAN_MANAGE_PERMISSIONS]: [KANBAN_MANAGE_PERMISSIONS],
};

// Atendente sem papel personalizado mantém o que sempre pôde: ler e mexer em
// card. Trocar isso tiraria o Kanban de todo atendente de toda conta.
const PLAIN_AGENT_DEFAULTS = [
  KANBAN_ACCESS_PERMISSIONS,
  KANBAN_DEAL_MANAGE_PERMISSIONS,
];

// Lista que um guard de rota / item de menu aceita pra este degrau: o
// `hasPermissions` do projeto é OR, então a implicação vira "qualquer um destes".
export const kanbanRoutePermissions = permission => [
  'administrator',
  ...IMPLIED_BY[permission],
];

const isPlainAgent = permissions =>
  permissions.length === 1 && permissions[0] === 'agent';

export const kanbanAllowed = (permission, permissions = []) => {
  if (!permissions.length) return false;
  if (permissions.includes('administrator')) return true;
  if (isPlainAgent(permissions)) {
    return PLAIN_AGENT_DEFAULTS.includes(permission);
  }
  return IMPLIED_BY[permission].some(p => permissions.includes(p));
};

export const canAccessKanban = permissions =>
  kanbanAllowed(KANBAN_ACCESS_PERMISSIONS, permissions);

const restrictedBy = (permission, permissions) =>
  !permissions.includes('administrator') &&
  !isPlainAgent(permissions) &&
  permissions.includes(permission);

export const kanbanOwnDealsOnly = (permissions = []) =>
  restrictedBy(KANBAN_OWN_DEALS_ONLY_PERMISSIONS, permissions);

export const canViewKanbanAnalytics = permissions =>
  canAccessKanban(permissions) &&
  !restrictedBy(KANBAN_ANALYTICS_BLOCK_PERMISSIONS, permissions) &&
  !kanbanOwnDealsOnly(permissions);

export const canManageDeals = permissions =>
  kanbanAllowed(KANBAN_DEAL_MANAGE_PERMISSIONS, permissions);

export const canManageKanban = permissions =>
  kanbanAllowed(KANBAN_MANAGE_PERMISSIONS, permissions);
